import React, { useState } from 'react'
import {
  Drawer,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Close,
  TextFields,
  Image,
  Keyboard,
  TipsAndUpdates,
} from '@mui/icons-material'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

const markdownTips = [
  {
    category: 'Text Formatting',
    items: [
      {
        syntax: '**bold text**',
        description: 'Bold text',
        example: 'This is **bold**',
      },
      {
        syntax: '*italic text*',
        description: 'Italic text',
        example: 'This is *italic*',
      },
      {
        syntax: '~~strikethrough~~',
        description: 'Strikethrough text',
        example: '~~crossed out~~',
      },
      {
        syntax: '`inline code`',
        description: 'Inline code',
        example: 'Use `console.log()`',
      },
    ],
  },
  {
    category: 'Headings',
    items: [
      {
        syntax: '# Heading 1',
        description: 'Large heading',
        example: '# Main Title',
      },
      {
        syntax: '## Heading 2',
        description: 'Medium heading',
        example: '## Section Title',
      },
      {
        syntax: '### Heading 3',
        description: 'Small heading',
        example: '### Subsection',
      },
    ],
  },
  {
    category: 'Lists',
    items: [
      {
        syntax: '- Item 1\n- Item 2',
        description: 'Bullet list',
        example: '- First item\n- Second item',
      },
      {
        syntax: '1. Item 1\n2. Item 2',
        description: 'Numbered list',
        example: '1. Step one\n2. Step two',
      },
    ],
  },
  {
    category: 'Links & More',
    items: [
      {
        syntax: '[Link text](https://example.com)',
        description: 'Clickable link',
        example: '[FOAMfrat](https://foamfrat.com)',
      },
      {
        syntax: '> Quote text',
        description: 'Block quote',
        example: '> Important note',
      },
      { syntax: '---', description: 'Horizontal line', example: '---' },
    ],
  },
  {
    category: 'Citations',
    items: [
      {
        syntax: '{cite: Reference text}',
        description:
          'Cite a source inline. Right-click in a text block and choose "Insert citation" to add one automatically.',
        example:
          'Depth matters{cite: Smith J. Compression depth. 2020.} in arrest.',
      },
    ],
  },
]

const blockTips = [
  {
    title: 'Text Blocks',
    icon: <TextFields />,
    tips: [
      'Use markdown shortcuts for quick formatting',
      'Press Enter twice to create a new paragraph',
      'Select text to see the formatting toolbar with markdown buttons',
      'Context menu appears when highlighting text - click buttons to apply styles',
      'Start with # for instant headings',
      'Right-click anywhere in a text block to insert a citation',
      'Citations show as raw {cite: ...} text while editing - use Preview to see the numbered reference and References list',
    ],
  },
  {
    title: 'Image Blocks',
    icon: <Image />,
    tips: [
      'Hover over images to see size controls',
      'Three size options: Small (33%), Medium (66%), Large (100%)',
      'Click below image to add captions',
      'Drag blocks to reorder content',
    ],
  },
]

interface EnhancedHelpPanelProps {
  isOpen: boolean
  onClose: () => void
}

export function EnhancedHelpPanel({ isOpen, onClose }: EnhancedHelpPanelProps) {
  let [tabValue, setTabValue] = useState(0)

  let handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 420,
            maxWidth: '90vw',
            top: 80, // Account for 80px app bar
            height: 'calc(100vh - 80px)', // Adjust height to prevent overflow
          },
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Show Notes Help
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab
              icon={<Keyboard />}
              label="Shortcuts"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<TipsAndUpdates />}
              label="Tips"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {/* Markdown Shortcuts Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use these markdown shortcuts to format your text quickly in text
            blocks:
          </Typography>

          {markdownTips.map((section, index) => (
            <Box key={section.category} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                {section.category}
              </Typography>

              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                {section.items.map((tip, tipIndex) => (
                  <ListItem
                    key={tipIndex}
                    sx={{
                      py: 1,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Chip
                        label={tip.syntax}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" component="span">
                        {tip.description}
                      </Typography>
                    </Box>
                    {tip.example && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'action.hover',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          width: '100%',
                        }}
                      >
                        Example: {tip.example}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>

              {index < markdownTips.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}
        </TabPanel>

        {/* Tips Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Pro tips for using the block-based editor effectively:
          </Typography>

          {blockTips.map((section) => (
            <Card key={section.title} elevation={1} sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  {section.icon}
                  <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                    {section.title}
                  </Typography>
                </Box>
                <List dense>
                  {section.tips.map((tip, index) => (
                    <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                      <ListItemText
                        primary={`• ${tip}`}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            sx: { fontSize: '0.875rem' },
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}

          {/* Pro Tip */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              mt: 3,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Pro Tip
            </Typography>
            <Typography variant="body2">
              Highlight any text in a text block to see a context menu with
              formatting buttons (bold, italic, headings, etc.) - no need to
              remember markdown syntax! You can also drag blocks to reorder
              them.
            </Typography>
          </Box>
        </TabPanel>
      </Box>
    </Drawer>
  )
}

export default EnhancedHelpPanel
